import React, { useCallback, useEffect, useRef, useState } from 'react'
import Dropzone from 'react-dropzone'
import Papa from 'papaparse'
import * as Icons from 'react-feather'
import { toast } from 'sonner'
import CircularProgress from '@material-ui/core/CircularProgress'
import InputEnter from '../generic/InputEnter'
import Button from '../generic/Button'
import { getSelectedClients } from '../data/interactions'
import { requestAPI } from '../data/api'
import NoClientsSelected from './NoClientsSelected'

const LIST_LIMIT = 50 // most recent handouts fetched per (search) request
const SEARCH_DEBOUNCE_MS = 300
const SAMPLE_ROWS = 5 // client image IDs previewed before a CSV import

export default function Handouts({ client_ids }) {
	const [imageId, setImageId] = useState('')
	const [marking, setMarking] = useState(false)
	const [search, setSearch] = useState('')
	const [list, setList] = useState({ status: 'not-loaded', total: 0, data: [] })
	const [upload, setUpload] = useState(null) // { fileName, status, ids, summary }
	const requestRef = useRef(0)

	const selectedClients = getSelectedClients()
	const clientId = selectedClients.length === 1 ? selectedClients[0].client_id : null

	const loadHandouts = useCallback(
		async (query) => {
			if (!clientId) return
			const requestId = ++requestRef.current
			setList((current) => ({ ...current, status: 'loading' }))
			const response = await getHandouts(clientId, query)
			if (requestId !== requestRef.current) return // a newer request won
			const handouts = readHandouts(response)
			if (!handouts) {
				setList({ status: 'loaded', total: 0, data: [] })
				toast.error('Could not load the handouts')
				return
			}
			setList({
				status: 'loaded',
				total: typeof response.total === 'number' ? response.total : handouts.length,
				data: handouts,
			})
		},
		[clientId],
	)

	// Nothing on the page survives a client switch — the list belongs to the
	// client it was loaded for.
	useEffect(() => {
		setSearch('')
		setImageId('')
		setUpload(null)
		setList({ status: 'not-loaded', total: 0, data: [] })
	}, [clientId])

	useEffect(() => {
		if (!clientId) return
		const timeout = setTimeout(() => loadHandouts(search), SEARCH_DEBOUNCE_MS)
		return () => clearTimeout(timeout)
	}, [clientId, search, loadHandouts])

	if (!client_ids || client_ids.length === 0) return <NoClientsSelected />

	const onMarkSingle = async (value) => {
		const clientImageId = (value || '').trim()
		if (!clientImageId || marking) return
		setMarking(true)
		const result = await markImageAsHandout(clientId, clientImageId)
		setMarking(false)
		if (result === 'already-marked') {
			toast.warning(`${clientImageId} is already marked as handout`)
			return
		}
		if (result === 'error') {
			toast.error(`Could not mark ${clientImageId} as handout`)
			return
		}
		toast.success(`${clientImageId} has been marked as handout`)
		setImageId('')
		await loadHandouts(search)
	}

	const onDrop = (acceptedFiles, rejectedFiles) => {
		if (rejectedFiles && rejectedFiles.length > 0) {
			toast.error('Unsupported file. Please upload a .csv file.')
			return
		}
		const file = acceptedFiles[0]
		if (!file) return
		setUpload({ fileName: file.name, status: 'parsing', ids: [], summary: null })
		Papa.parse(file, {
			download: false,
			skipEmptyLines: true,
			complete: (csv) => onParsed(file, csv),
			error: () => {
				toast.error(`Could not read ${file.name}`)
				setUpload(null)
			},
		})
	}

	const onParsed = (file, csv) => {
		const ids = csv.data.map((row) => (row[0] || '').trim()).filter(Boolean)
		if (ids.length === 0) {
			toast.error('The file does not contain any client image IDs')
			setUpload(null)
			return
		}
		// Marking closes cases and writes to Pipedrive, so the IDs we read out of
		// the file are shown for confirmation before anything is submitted.
		setUpload({ fileName: file.name, status: 'confirming', ids, summary: null })
	}

	const onConfirmImport = async () => {
		const { fileName, ids } = upload
		setUpload({ fileName, status: 'uploading', ids, summary: null })
		const summary = await markImagesAsHandout(clientId, ids)
		if (!summary) {
			toast.error('Could not mark the images as handout')
			setUpload(null)
			return
		}
		setUpload({ fileName, status: 'done', ids, summary })
		toast.success(`${summary.numberOfImagesMarkedAsHandout} image(s) marked as handout`, {
			description: `${summary.numberOfImagesAlreadyMarkedAsHandout} already marked · ${summary.numberOfDuplicateImages} duplicate`,
		})
		await loadHandouts(search)
	}

	const onRemove = async (handout) => {
		const result = await unmarkAsHandout(clientId, handout.client_image_id)
		if (result === 'error') {
			toast.error(`Could not remove the handout on ${handout.client_image_id}`)
			return false
		}
		if (result === 'not-marked') {
			toast.warning(`${handout.client_image_id} is no longer marked as handout`)
		} else {
			toast.success(`${handout.client_image_id} is no longer marked as handout`)
		}
		await loadHandouts(search)
		return true
	}

	const busy = upload && (upload.status === 'parsing' || upload.status === 'uploading')

	return (
		<div id='handouts' className='container'>
			<h1 style={{ marginTop: '30px', marginBottom: '20px' }}>Handouts</h1>
			<p className='ho-intro'>
				Marking an image as handout stops the system from detecting cases for that
				image, closes its cases in review and adds an activity about the handout to
				the cases in Pipedrive. Removing a handout lets the image be scanned again —
				it does not reopen the cases that were already closed.
			</p>

			{!clientId ? (
				<div className='ho-notice'>
					<Icons.AlertCircle size={16} />
					<span>
						Handouts are managed one client at a time. Select a single client to
						continue.
					</span>
				</div>
			) : (
				<div className='row'>
					{/* ---- Left: mark single / import ---- */}
					<div className='col-md-5'>
						<div className='ho-panel'>
							<label className='ho-label'>Mark a single image as handout</label>
							<InputEnter
								value={imageId}
								disabled={marking}
								onChange={(e) => setImageId(e.target.value)}
								onEnter={onMarkSingle}
								placeholder='Client image ID'
							/>
						</div>

						<div className='ho-panel'>
							<label className='ho-label'>Mark multiple images as handout</label>
							<p className='ho-hint'>
								A CSV file with the client image IDs in the first column, one per
								row.
							</p>

							{busy ? (
								<div className='ho-dropzone ho-dropzone-busy'>
									<CircularProgress size={26} />
									<span className='ho-dropzone-text'>
										{upload.status === 'parsing'
											? `Reading ${upload.fileName}…`
											: `Marking ${upload.ids.length} image(s) as handout…`}
									</span>
								</div>
							) : upload && upload.status === 'confirming' ? (
								<ImportConfirmation
									upload={upload}
									onConfirm={onConfirmImport}
									onCancel={() => setUpload(null)}
								/>
							) : (
								<Dropzone
									onDrop={onDrop}
									multiple={false}
									accept='text/csv,application/vnd.ms-excel,.csv'
									className='ho-dropzone'
									activeClassName='ho-dropzone-active'
									rejectClassName='ho-dropzone-reject'
								>
									<Icons.UploadCloud size={28} />
									<span className='ho-dropzone-text'>
										Drag a CSV here, or click to choose a file
									</span>
								</Dropzone>
							)}

							{upload && upload.status === 'done' && (
								<ImportResults
									summary={upload.summary}
									onDismiss={() => setUpload(null)}
								/>
							)}
						</div>
					</div>

					{/* ---- Right: current handouts ---- */}
					<div className='col-md-7'>
						<div className='ho-list-header'>
							<div className='ho-search'>
								<Icons.Search size={16} className='ho-search-icon' />
								<input
									className='ho-search-input'
									type='text'
									value={search}
									placeholder='Search client image IDs…'
									onChange={(e) => setSearch(e.target.value)}
								/>
								{search && (
									<button
										className='ho-search-clear'
										onClick={() => setSearch('')}
										title='Clear'
									>
										<Icons.X size={15} />
									</button>
								)}
							</div>
							<span className='ho-count'>
								{list.total > list.data.length
									? `${list.data.length} of ${list.total}`
									: `${list.total} image${list.total === 1 ? '' : 's'}`}
							</span>
						</div>

						{list.status !== 'loaded' && list.data.length === 0 ? (
							<div className='ho-empty'>
								<CircularProgress size={28} />
							</div>
						) : list.data.length === 0 ? (
							<div className='ho-empty'>
								{search
									? 'No handouts match your search.'
									: 'No images are marked as handout.'}
							</div>
						) : (
							<>
								{/* Previous results stay up, dimmed, while a search reloads. */}
								<div
									className='ho-list'
									style={{ opacity: list.status === 'loading' ? 0.5 : 1 }}
								>
									{list.data.map((handout) => (
										<HandoutRow
											key={handout.id}
											handout={handout}
											onRemove={onRemove}
										/>
									))}
								</div>
								{list.total > list.data.length && (
									<p className='ho-hint'>
										Showing the {LIST_LIMIT} most recent. Search to find a
										specific client image ID.
									</p>
								)}
							</>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

function HandoutRow({ handout, onRemove }) {
	const [confirming, setConfirming] = useState(false)
	const [removing, setRemoving] = useState(false)

	const remove = async () => {
		setRemoving(true)
		const removed = await onRemove(handout)
		// On success the row disappears with the refreshed list; on failure we stay
		// mounted and let the user try again.
		if (!removed) {
			setRemoving(false)
			setConfirming(false)
		}
	}

	return (
		<div className='ho-row'>
			<div className='ho-row-main'>
				<span className='ho-row-id' title={handout.client_image_id}>
					{handout.client_image_id}
				</span>
				<span className='ho-row-meta'>{describeMarking(handout)}</span>
			</div>
			{confirming ? (
				<span className='ho-row-actions'>
					<button
						className='ho-icon-btn ho-confirm-yes'
						onClick={remove}
						disabled={removing}
						title='Confirm removal'
					>
						{removing ? <CircularProgress size={14} /> : <Icons.Check size={15} />}
					</button>
					<button
						className='ho-icon-btn ho-confirm-no'
						onClick={() => setConfirming(false)}
						disabled={removing}
						title='Cancel'
					>
						<Icons.X size={15} />
					</button>
				</span>
			) : (
				<button
					className='ho-icon-btn ho-row-delete'
					onClick={() => setConfirming(true)}
					title='Remove handout'
				>
					<Icons.Trash2 size={15} />
				</button>
			)}
		</div>
	)
}

// Images handed out outside the backoffice have no marking event to name.
function describeMarking({ email, occurred_at }) {
	if (!occurred_at) return 'No record of who marked this image'
	const date = new Date(occurred_at).toLocaleString()
	return email ? `${email} · ${date}` : date
}

function ImportConfirmation({ upload, onConfirm, onCancel }) {
	const { fileName, ids } = upload
	return (
		<div className='ho-confirm'>
			<div className='ho-confirm-head'>
				<span>
					<b>{ids.length}</b> client image ID(s) found in {fileName}
				</span>
			</div>
			<ul className='ho-confirm-list'>
				{ids.slice(0, SAMPLE_ROWS).map((id, index) => (
					<li key={`${id}-${index}`}>{id}</li>
				))}
				{ids.length > SAMPLE_ROWS && (
					<li className='ho-confirm-more'>+ {ids.length - SAMPLE_ROWS} more</li>
				)}
			</ul>
			<div className='ho-confirm-actions'>
				<Button onClick={onConfirm}>Mark as handout</Button>
				<button className='ho-text-btn' onClick={onCancel}>
					Cancel
				</button>
			</div>
		</div>
	)
}

function ImportResults({ summary, onDismiss }) {
	const groups = [
		{
			label: 'Marked as handout',
			count: summary.numberOfImagesMarkedAsHandout,
			tone: 'ok',
		},
		{
			label: 'Already marked as handout',
			count: summary.numberOfImagesAlreadyMarkedAsHandout,
			tone: 'warn',
		},
		{
			label: 'Duplicates in the file',
			count: summary.numberOfDuplicateImages,
			tone: 'warn',
		},
	]
	return (
		<div className='ho-results'>
			<div className='ho-results-head'>
				<span>Import complete</span>
				<button className='ho-icon-btn' onClick={onDismiss} title='Dismiss'>
					<Icons.X size={15} />
				</button>
			</div>
			{groups.map((group) => (
				<div key={group.label} className={`ho-result-row ho-tone-${group.tone}`}>
					<span className='ho-chip'>{group.count}</span>
					<span>{group.label}</span>
				</div>
			))}
		</div>
	)
}

async function getHandouts(clientId, search) {
	const query = search ? `?search=${encodeURIComponent(search)}&limit=${LIST_LIMIT}` : `?limit=${LIST_LIMIT}`
	return requestAPI(`/handout/${clientId}${query}`, 'GET')
}

// The backoffice and the API deploy separately, so an older API may still
// answer with a bare array instead of { total, handouts }.
function readHandouts(response) {
	if (Array.isArray(response)) return response
	if (response && Array.isArray(response.handouts)) return response.handouts
	return null
}

async function markImageAsHandout(clientId, clientImageId) {
	const response = await requestAPI('/handout/', 'POST', {
		client_id: clientId,
		client_image_id: clientImageId,
	})
	if (response && response.code === 'image_already_marked_as_hand_out')
		return 'already-marked'
	if (failed(response)) return 'error'
	return 'marked'
}

async function markImagesAsHandout(clientId, clientImageIds) {
	const response = await requestAPI('/handout/mark-images-as-handout', 'POST', {
		client_id: clientId,
		client_image_ids: clientImageIds,
	})
	if (failed(response) || typeof response.numberOfImagesMarkedAsHandout !== 'number')
		return null
	return response
}

async function unmarkAsHandout(clientId, clientImageId) {
	const response = await requestAPI('/handout/', 'DELETE', {
		client_id: clientId,
		client_image_id: clientImageId,
	})
	if (response && response.code === 'image_not_marked_as_hand_out')
		return 'not-marked'
	if (failed(response)) return 'error'
	return 'unmarked'
}

// requestAPI hands back parsed JSON when the response has one and the raw
// Response otherwise, so both shapes have to be checked for failure.
function failed(response) {
	if (!response) return true
	if (response.code) return true
	return typeof response.status === 'number' && response.status >= 400
}
